import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { initials } from "@/app/nav";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { RequestError } from "@/app/queries";
import { useStudentCard, type StudentCard } from "@/app/reviewQueries";
import { useMe } from "@/app/session";
import { NoAccess, NotFound, SkeletonRows, StateCard } from "@/ui/states";
import { Notes } from "./mentor/student/Notes";
import { Repeats } from "./mentor/student/Repeats";
import { Interviews } from "./mentor/student/Interviews";
import { RiskCard } from "./mentor/student/RiskCard";
import { Submissions } from "./mentor/student/Submissions";
import { FlagChips, RouteSoon, Summary } from "./mentor/student/Summary";
import { isPending, sortSubmissions } from "./mentor/student/model";

/** M3 «Студент подробно»: шапка, риск, сводка, сдачи, повторяющиеся замечания,
 *  приватные заметки. Маршрут и гейты — честная заглушка до SP5. */
export function StudentPage({ userId }: { userId: string }) {
  const me = useMe();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const card = useStudentCard(userId);
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );
  const err = card.error;
  if (err instanceof RequestError && err.status === 403)
    return frame(<NoAccess where="карточка студента" body="Карточку студента с приватными заметками видят менторы и администраторы школы." primary={{ label: "на мой маршрут", href: "/" }} />);
  if (err instanceof RequestError && err.status === 404) return frame(<NotFound primary={{ label: "к очереди", href: "/mentor/queue" }} />);
  if (err)
    return frame(
      <StateCard
        code="ERR_NETWORK"
        where="карточка студента"
        tone="warn"
        title="Нет связи с сервером"
        body="Карточка не загрузилась. Сдачи и заметки хранятся на сервере — ничего не потеряно."
        primary={{ label: "повторить сейчас", onClick: () => void card.refetch() }}
      />,
    );
  if (!card.data) return frame(<SkeletonRows />);
  return <Student card={card.data} userId={userId} user={user} />;
}

function Student({ card, userId, user }: { card: StudentCard; userId: string; user?: { name: string; role: string } }) {
  const now = Date.now();
  const pending = sortSubmissions(card.submissions).find(isPending);
  const cohort = card.cohort || "без когорты";
  return (
    <Shell nav wide user={user}>
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-5 py-2.5">
        <span className="font-mono text-[12px] text-mut" data-testid="breadcrumb">
          <Link to="/mentor/queue" className="text-mut no-underline hover:text-ink">
            очередь
          </Link>{" "}
          / {card.student.name}
        </span>
        <span className="flex-1" />
        {pending && (
          <Link
            to="/mentor/review/$submissionId"
            params={{ submissionId: pending.id }}
            className="inline-flex items-center rounded-sm border border-acc bg-acc px-3 py-[7px] font-mono text-[12px] font-medium text-accink no-underline hover:brightness-[1.08]"
            data-testid="open-pending"
          >
            открыть сдачу · v{pending.iteration}
          </Link>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="flex flex-col gap-4 border-bd px-5 py-[22px] lg:border-r">
          <div className="flex items-center gap-3">
            <span aria-hidden className="grid size-[46px] place-items-center rounded-full border border-bd2 bg-card2 font-mono text-[15px] font-semibold text-ink">
              {initials(card.student.name)}
            </span>
            <div className="flex min-w-0 flex-col">
              <h1 className="m-0 truncate font-mono text-[17px] font-bold tracking-[-.01em]">{card.student.name}</h1>
              <span className="font-mono text-[11.5px] text-ink2" data-testid="student-meta">
                {cohort} · ментор {card.mentor?.name ?? "не назначен"}
              </span>
            </div>
          </div>
          <FlagChips card={card} now={now} />
          {card.flag && <RiskCard flag={card.flag} />}
          <Summary card={card} />
          <RouteSoon />
        </aside>
        <div className="flex min-w-0 flex-col gap-7 px-[22px] py-5">
          <Submissions subs={card.submissions} now={now} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
            <Notes userId={userId} notes={card.notes} />
            <Repeats repeats={card.repeats} />
          </div>
          <div>
            <Interviews userId={userId} />
          </div>
        </div>
      </div>
    </Shell>
  );
}
